/**
 * Copyright (c) 2020-2021 Perficient, Inc.
 * Unauthorized copying or distribution of this file prohibited without the express permission of Perficient.
 */

import React, { useEffect, useState, useCallback, useMemo } from "react";
import { Col, Row, Form } from "react-bootstrap";
import { Typeahead } from "react-bootstrap-typeahead";
import { v2, errorToObject } from "@prftesp/cle-logger";
import errorCodes from "../../../../utility/errorCodes";
import { getTeamsUserStatuses } from "../../services/TeamsService";
import {
	getExternalContacts,
	getTeamsConversationStatus,
	startTeamsConversation,
} from "../../services/ExternalContactsService";
import { LoadingButton } from "../Common/LoadingButton";
import { useCurrentContactActions } from "../../hooks/useCurrentContactActions";
import { ExternalContactCard } from "./ExternalContactCard";
import useAgent from "../../hooks/useAgent";
import { redactPII } from "../../../../utility/helpers";
import { PII_FIELDS } from "../../../../utility/constants";

const COMPONENT_NAME = "CCP components/ExternalTransfers";
const CONTACTS_PER_PAGE = 100;

const isSearchMatch = ({ fullName, phone, tags }, lowerCasedSearchTerm, tagSearchTerms) => {
	const matchesName = lowerCasedSearchTerm ? fullName.toLowerCase().includes(lowerCasedSearchTerm) : true;
	const matchesPhone = lowerCasedSearchTerm ? phone && phone.includes(lowerCasedSearchTerm) : true;
	const matchesTag = tagSearchTerms.length > 0 ? tags.some((t) => tagSearchTerms.includes(t)) : true;

	return (matchesPhone || matchesName) && matchesTag;
};

const filterContacts = (searchTerm, tagSearchTerms, contacts) => {
	const lowerCasedSearchTerm = searchTerm?.toLowerCase() || "";
	return contacts.filter((contact) => isSearchMatch(contact, lowerCasedSearchTerm, tagSearchTerms));
};

/**
 * Displays a list of external contacts and handles voice transfers
 * @param {Object} props
 * @param {Boolean} props.transfersEnabled whether or not the transfer buttons on the contacts are enabled
 * @param {(shouldShow, message, level) => void} props.renderToastContent displays a toast message with the given level (success, warning or danger)
 */
export const ExternalTransfers = ({ transfersEnabled, renderToastContent }) => {
	const [externalContacts, setExternalContacts] = useState([]);
	const [selectedTags, setSelectedTags] = useState([]);
	const [searchInput, setSearchInput] = useState("");
	const [displayedContacts, setDisplayedContacts] = useState([]);
	const [isSearchingContacts, setIsSearchingContacts] = useState(false);
	const [isRefreshingAll, setIsRefreshingAll] = useState(false);
	const [pageNo, setPageNo] = useState(1);
	const currentAgent = useAgent();

	const { transferToNumber } = useCurrentContactActions();
	const hasMoreContacts = displayedContacts.length > pageNo * CONTACTS_PER_PAGE;

	const tagOptions = useMemo(() => {
		const tagSet = new Set();
		externalContacts.forEach(({ tags }) => {
			if (!tags) {
				return;
			}
			tags.forEach((t) => tagSet.add(t));
		});
		return [...tagSet];
	}, [externalContacts]);

	const getContactAzureUserIds = useCallback(
		(contacts) => contacts.map(({ azureAdUserId }) => azureAdUserId).filter((x) => !!x),
		[],
	);

	useEffect(() => {
		getExternalContacts()
			.then((contacts) => setExternalContacts(contacts))
			.catch((error) => {
				v2.error("Error while loading external contacts.", {
					additionalData: { error: errorToObject(error) },
					componentName: COMPONENT_NAME,
					logCode: errorCodes.GET_EXTERNAL_CONTACTS_ERROR,
				});
				renderToastContent(true, "Failed to load contacts.", "danger");
				setExternalContacts([]);
			});
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	const handleSearch = useCallback(
		async (e) => {
			e.preventDefault();
			try {
				if (searchInput.length < 3 && selectedTags.length === 0) {
					renderToastContent(
						true,
						"Minimum of 3 search characters or 1 selected tag required for contact search.",
						"error",
					);
					return;
				}

				setIsSearchingContacts(true);

				const filteredContacts = filterContacts(searchInput, selectedTags, externalContacts);
				setPageNo(1);

				if (filteredContacts.length === 0) {
					setDisplayedContacts([]);
					setIsSearchingContacts(false);
					return;
				}

				const azureUserIds = getContactAzureUserIds(filteredContacts);

				const statuses = await getTeamsUserStatuses(azureUserIds);

				const contactsWithStatus = filteredContacts.map((contact) => {
					const status = statuses[contact.azureAdUserId];

					const activity = status?.activity || null;

					return { ...contact, activity };
				});

				setDisplayedContacts(contactsWithStatus);
			} catch (error) {
				v2.error("Error while searching contacts.", {
					additionalData: { error: errorToObject(error), searchInput, selectedTags },
					componentName: COMPONENT_NAME,
					logCode: errorCodes.GET_EXTERNAL_CONTACTS_ERROR,
				});
				renderToastContent(true, "Error while searching contacts, please try again.", "danger");
			}
			setIsSearchingContacts(false);
		},
		[selectedTags, searchInput, externalContacts, getContactAzureUserIds, renderToastContent],
	);

	const incrementPageNo = (e) => {
		e.preventDefault();
		if (hasMoreContacts) {
			setPageNo((current) => current + 1);
		}
	};

	const refreshDisplayedActivity = useCallback(async () => {
		const azureUserIds = getContactAzureUserIds(displayedContacts);
		const statuses = await getTeamsUserStatuses(azureUserIds);
		const conversationStatuses = await getTeamsConversationStatus(azureUserIds);

		const newDisplayContacts = displayedContacts.map((contact) => {
			const status = statuses[contact.azureAdUserId];
			const conversationStatus = conversationStatuses[contact.azureAdUserId];

			const activity = status?.activity || null;

			return { ...contact, activity, teamsInfo: { ...contact.teamsInfo, inActiveConversation: conversationStatus } };
		});

		setDisplayedContacts(newDisplayContacts);
		return newDisplayContacts;
	}, [displayedContacts, getContactAzureUserIds]);

	const onRefreshActivity = useCallback(async () => {
		try {
			setIsRefreshingAll(true);
			await refreshDisplayedActivity();
		} catch (error) {
			v2.error("Error while refreshing contact activity.", {
				additionalData: { error: errorToObject(error) },
				componentName: COMPONENT_NAME,
				logCode: errorCodes.REFRESH_CONTACT_ACTIVITY_ERROR,
			});
			renderToastContent(true, "Error while fetching contact availability.", "danger");
		}
		setIsRefreshingAll(false);
	}, [refreshDisplayedActivity, renderToastContent]);

	const onStartTransfer = useCallback(
		async (externalContact) => {
			try {
				await transferToNumber(externalContact.phone);
				renderToastContent(true, "Successfully started transfer.", "success");
			} catch (error) {
				v2.error("Error while transferring to external contact.", {
					additionalData: { error, externalContact: redactPII(externalContact, PII_FIELDS.externalContact) },
					componentName: COMPONENT_NAME,
					logCode: errorCodes.START_EXTERNAL_TRANSFER_ERROR,
				});
				renderToastContent(true, "Transfer to contact failed, please try again.", "danger");
			}
		},
		[transferToNumber, renderToastContent],
	);

	const onStartTeamsChat = useCallback(
		async (externalContact) => {
			try {
				await startTeamsConversation(
					externalContact.azureAdUserId,
					externalContact.fullName,
					currentAgent?.getConfiguration().username,
				);
				renderToastContent(true, "Started a teams conversation.", "success");

				// update data sets to reflect this user being in conversation
				let newContacts = [...externalContacts];
				const contactIdx = newContacts.findIndex((contact) => contact.contactId === externalContact.contactId);
				newContacts[contactIdx].teamsInfo.inActiveConversation = true;
				setExternalContacts(newContacts);

				let newDisplayedContacts = [...displayedContacts];
				const displayContactIdx = newDisplayedContacts.findIndex(
					(contact) => contact.contactId === externalContact.contactId,
				);
				newDisplayedContacts[displayContactIdx].teamsInfo.inActiveConversation = true;
				setDisplayedContacts(newDisplayedContacts);
			} catch (error) {
				renderToastContent(true, "Failed to start a teams conversation.", "error");

				v2.error("Error while starting teams conversation.", {
					additionalData: {
						error: errorToObject(error),
						externalContact: redactPII(externalContact, PII_FIELDS.externalContact),
					},
					componentName: COMPONENT_NAME,
					logCode: errorCodes.START_TEAMS_CONVERSATION_ERROR,
				});
			}
		},
		[renderToastContent, externalContacts, displayedContacts, currentAgent],
	);

	return (
		<div>
			<Row noGutters style={{ padding: "15px" }}>
				<Col sm={12}>
					<Form onSubmit={handleSearch}>
						<Form.Row>
							<Col xl={5} lg={12}>
								<Form.Group>
									<Form.Label>Search by name or phone #</Form.Label>
									<Form.Control
										autoComplete="new-password" // prevents showing auto fill dropdown in Chrome
										type="text"
										value={searchInput}
										onChange={(e) => setSearchInput(e.target.value)}
									/>
								</Form.Group>
							</Col>
							<Col xl={5} lg={12}>
								<Form.Group>
									<Form.Label>Search by tag</Form.Label>
									<Typeahead
										id="tag-search"
										multiple
										onChange={(selected) => setSelectedTags([...selected])}
										options={tagOptions}
										selected={selectedTags}
									/>
								</Form.Group>
							</Col>
							<Col xl={2} lg={12} className="align-self-end">
								<Form.Group>
									<LoadingButton
										text="Search"
										isLoading={isSearchingContacts}
										variant="outline-secondary"
										type="submit"
									/>
								</Form.Group>
							</Col>
						</Form.Row>
					</Form>
				</Col>
			</Row>
			<Row noGutters style={{ padding: "0px 15px" }}>
				{!displayedContacts.length && (
					<div style={{ color: "#9ca3af" }}>
						<div className="d-inline w-100">
							<h5 className="ml-0">No contacts found</h5>
							<span>Use the search option above to find an external contact by name, phone number or tag.</span>
						</div>
					</div>
				)}
				{displayedContacts.length > 0 && (
					<Col style={{ padding: "0px" }}>
						<div
							className="d-flex align-items-center justify-content-between pb-3"
							style={{ borderBottom: "1px solid #ccc", marginBottom: 12 }}
						>
							<div>{displayedContacts.length} contact(s) found:</div>
							<LoadingButton
								text="Refresh availability"
								isLoading={isRefreshingAll}
								variant="outline-secondary"
								onClick={onRefreshActivity}
								size="sm"
							/>
						</div>
						{displayedContacts.map(
							(c, idx) =>
								idx < pageNo * CONTACTS_PER_PAGE && (
									<ExternalContactCard
										key={c.contactId}
										transfersEnabled={transfersEnabled}
										externalContact={c}
										onStartTransfer={onStartTransfer}
										onStartTeamsChat={onStartTeamsChat}
										refreshDisplayedActivity={refreshDisplayedActivity}
									/>
								),
						)}
						{hasMoreContacts && (
							<div className="text-muted d-flex align-items-center justify-content-center">
								<button className="btn btn-link px-1 shadow-none" onClick={incrementPageNo}>
									Click here
								</button>
								<span> to load more contacts. </span>
							</div>
						)}
					</Col>
				)}
			</Row>
		</div>
	);
};
